// ===========================================================================
// One picker for every deck.
//
// Every deck under decks/ answers the same two questions: "a card this room
// has not seen" and "n distinct cards". Writing the exclusion logic once means
// every deck falls back the same way -- when a room has burned through the
// whole deck it starts over rather than answering nothing, because a game that
// stalls on its 81st card is worse than a repeat.
//
// Decks are keyed on a string so a caller can remember what it has shown
// without holding the card objects: a plain card IS its key; a pair is
// "a / b"; a trivia question is its text. ASCII only.
// ===========================================================================

/** The string a caller stores to say "seen this one". */
export function cardKey(card) {
  if (typeof card === 'string') return card;
  if (card && typeof card === 'object') {
    if ('a' in card && 'b' in card) return `${card.a} / ${card.b}`;
    if ('left' in card && 'right' in card) return `${card.left} / ${card.right}`;
    if ('q' in card) return card.q;
  }
  return String(card);
}

/**
 * The deck for a band. Anything that is not exactly the adult band gets the
 * minor deck: an unknown band is a bug somewhere upstream, and the safe way to
 * be wrong is to show a 13-year-old's card to an adult, never the reverse.
 */
export function bandDeck(decks, band) {
  return band === 'adult' ? decks.adult : decks.minor;
}

const asSet = (x) => (x instanceof Set ? x : new Set(x || []));

/**
 * One card not in excludeSet, or any card when the deck is exhausted.
 * @param {ReadonlyArray<any>} list
 * @param {Set<string>|Iterable<string>} [excludeSet] keys already shown
 * @param {() => number} [rnd] injectable for tests
 */
export function pickOne(list, excludeSet = new Set(), rnd = Math.random) {
  if (!list || !list.length) return null;
  const seen = asSet(excludeSet);
  const fresh = list.filter((c) => !seen.has(cardKey(c)));
  const pool = fresh.length ? fresh : list;
  // The modulo guards a rnd() that returns exactly 1.
  return pool[Math.floor(rnd() * pool.length) % pool.length];
}

function shuffled(list, rnd) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rnd() * (i + 1)) % (i + 1);
    const t = out[i];
    out[i] = out[j];
    out[j] = t;
  }
  return out;
}

/**
 * Up to n distinct cards: the unseen ones first, in random order, topped up
 * from the seen ones only when the request is bigger than what is left.
 */
export function pickMany(list, n, excludeSet = new Set(), rnd = Math.random) {
  if (!list || !list.length || !(n > 0)) return [];
  const seen = asSet(excludeSet);
  const want = Math.min(Math.floor(n), list.length);
  const fresh = [];
  const old = [];
  const taken = new Set();
  for (const c of list) {
    const k = cardKey(c);
    if (taken.has(k)) continue; // a duplicate in the source list is one card
    taken.add(k);
    (seen.has(k) ? old : fresh).push(c);
  }
  const out = shuffled(fresh, rnd).slice(0, want);
  if (out.length < want) out.push(...shuffled(old, rnd).slice(0, want - out.length));
  return out;
}

export default { cardKey, bandDeck, pickOne, pickMany };
